/**
 * A finished scan read back from what was saved (DEC-185, UI_SDD §R.3).
 *
 * DEC-187 moved what this file used to render: the summary sentence is the banner's
 * headline and DEC-186's per-wave lines are the wave rows. What stays here is the copy
 * and the counting both of those share, so the saved wording lives in one place.
 * Every count on a saved scan is "after review": saved findings are post-Critic and can
 * differ from the live count.
 */
import type { AgentState } from '../../stores/scanStore'

/** DEC-185's note, carried by a saved scan's banner. */
export const SAVED_RUN_NOTE =
  'Rebuilt from the saved scan. Counts are after review, so they can differ from what the agents reported live. Time and tokens per agent were not recorded.'

/** DEC-195: a saved scan that recorded per-agent time and tokens. */
export const SAVED_RUN_NOTE_TIMED =
  'Rebuilt from the saved scan. Counts are after review, so they can differ from what the agents reported live.'

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

/** "3 done · 1 skipped". Only the outcomes that occurred, in a fixed order. */
export function waveOutcome(agents: AgentState[]): string {
  const done = agents.filter((a) => a.status === 'complete' || a.status === 'warned').length
  const problems = agents.filter((a) => a.status === 'done_with_problems').length
  const failed = agents.filter((a) => a.status === 'failed').length
  const skipped = agents.filter((a) => a.status === 'skipped').length
  // `waiting` in a finished wave means the agent never ran: on a saved scan the saved row
  // never mentions it; on a live one the scan ended before reaching it.
  const notRun = agents.filter((a) => a.status === 'waiting').length
  const parts = [
    done > 0 ? `${done} done` : null,
    problems > 0 ? `${problems} with problems` : null,
    failed > 0 ? `${failed} failed` : null,
    skipped > 0 ? `${skipped} skipped` : null,
    notRun > 0 ? `${notRun} not run` : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : 'no agents'
}

/** DEC-186 status for a finished wave: "DONE" when every agent finished, otherwise the
 *  counts, in `critical` when one failed and `warn` when one was skipped or not run. */
export function finishedWaveStatus(agents: AgentState[]): { text: string; tone: 'success' | 'warn' | 'critical' } {
  const allDone = agents.length > 0 && agents.every((a) => a.status === 'complete' || a.status === 'warned')
  if (allDone) return { text: 'Done', tone: 'success' }
  const allFinished = agents.length > 0 && agents.every((a) => a.status === 'complete' || a.status === 'warned' || a.status === 'done_with_problems')
  if (allFinished) return { text: waveOutcome(agents), tone: 'warn' }
  const failed = agents.some((a) => a.status === 'failed')
  return { text: waveOutcome(agents), tone: failed ? 'critical' : 'warn' }
}

/** "Architecture is running now", "Complexity and Security are running now". */
export function runningSentence(labels: string[], paused = false): string | null {
  if (labels.length === 0) return null
  const names =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
  const verb = labels.length === 1 ? 'is' : 'are'
  return `${names} ${verb} ${paused ? 'paused' : 'running now'}`
}
